// New return (Jack, 2026-11-04): staff make a return for a BargainFox customer
// from the Returns page. Find the order by its number, choose one reason for
// the whole return, tick the items (Select all; a quantity for lines of more
// than one), choose the return service, the number of labels and the day, and
// check the money before "Create return". The customer is emailed the label.
//
// The quote (POST /admin/returns/manual/quote) is asked again ~300 ms after any
// change, and only the latest answer counts. "Create return" stays grey until
// everything is chosen and the latest quote is good; the footer says what is
// still missing.
//
// Inside the return window the customer could do it themselves on the returns
// site: a warning says how long it has been since delivery, and "I understand…"
// must be ticked. Items already in an open request: "replace" ones can still be
// ticked (the new return cancels that request and voids its labels; the quote
// lists them, and create sends them in `replace`); "shipped" and "locked" ones
// can't, and say why.
//
// Where it goes back to (Jack, 2026-11-04): any warehouse with a return address
// (Settings › Returns), the one that shipped the order by default. Its services
// follow: no Royal Mail away from the return warehouse (the pool's labels are
// printed to it), none outside the UK. "Add the label by hand" is always there.
import { FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { X, Search, AlertTriangle, Mail, Plus } from "lucide-react";
import { useTranslation } from "../../i18n";
import {
    ManualItemRequest, ManualOrder, ManualOrderItem, ManualOrderLookup, ManualQuoteResult, ManualReason, ManualService, ReturnWindow,
    findManualReturnOrder, quoteManualReturn, createManualReturn, gbp, BY_HAND_SERVICE,
} from "../../api/returns";

interface Props {
    onClose: () => void;
    /** The return was made: the page refreshes its list and opens it. */
    onCreated: (reference: string) => void;
}

const QUOTE_DELAY_MS = 300;
const NOTE_MAX = 2000;
const TITLE_ID = "rt-new-title";

// The reason select's groups, in this order (problem "return" is change of mind).
const GROUPS: { problem: string; key: string }[] = [
    { problem: "return", key: "rt_new_group_mind" },
    { problem: "damaged", key: "rt_problem_damaged" },
    { problem: "wrong", key: "rt_problem_wrong" },
];

// Why units in another request can't be returned again (ManualItemRequest.why).
const WHY = new Set(["on_its_way", "received", "exchange", "completed", "money_moved"]);

// Answers to the create call that mean the quote is out of date: ask again so it shows what changed.
const REQUOTE_ON = new Set(["window_ack_required", "replace_ack_required", "request_moved_on", "qty_unavailable", "date_unavailable"]);

type FindError = { message: string; references: string[] } | null;
type T = (key: string, params?: Record<string, string | number>) => string;

/**
 * Ticking a line takes the units that are free. Units another open request holds are only taken
 * (replacing that request) when none are free, or when staff raise the quantity: "Select all"
 * never cancels another return on its own.
 */
const fullQty = (it: ManualOrderItem) => Math.max(0, it.free_qty > 0 ? it.free_qty : it.available_qty);
const canPick = (it: ManualOrderItem) => it.available_qty > 0;

const NewReturnDialog = ({ onClose, onCreated }: Props) => {
    const { t, language } = useTranslation();
    const numberRef = useRef<HTMLInputElement>(null);
    const reasonRef = useRef<HTMLSelectElement>(null);
    const allRef = useRef<HTMLInputElement>(null);

    const [number, setNumber] = useState("");
    const [finding, setFinding] = useState(false);
    const [findError, setFindError] = useState<FindError>(null);
    const [found, setFound] = useState<ManualOrderLookup | null>(null);

    const [reasonCode, setReasonCode] = useState("");
    const [picked, setPicked] = useState<Record<number, number>>({});
    const [serviceId, setServiceId] = useState<string | null>(null);
    const [labels, setLabels] = useState(1);
    // Where it goes back to (Settings › Returns › Return addresses): the warehouse that shipped it by default.
    const [returnWarehouse, setReturnWarehouse] = useState<string | null>(null);
    const [day, setDay] = useState<string | null>(null);
    const [note, setNote] = useState("");
    const [ack, setAck] = useState(false);

    const [quote, setQuote] = useState<ManualQuoteResult | null>(null);
    const [quoteError, setQuoteError] = useState<string | null>(null);
    const [quoting, setQuoting] = useState(false);
    // Bumped to ask for the quote again with the same choices (create found it out of date).
    const [requote, setRequote] = useState(0);
    const quoteSeq = useRef(0);

    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const creatingRef = useRef(false);

    const locale = language === "pl" ? "pl-PL" : "en-GB";
    const dateFmt = useMemo(() => new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }), [locale]);
    const dayFmt = useMemo(() => new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short" }), [locale]);
    const fmtDate = (v: string | null | undefined) => (v ? dateFmt.format(new Date(`${v.slice(0, 10)}T12:00:00`)) : "");
    const fmtDay = (v: string) => dayFmt.format(new Date(`${v.slice(0, 10)}T12:00:00`));

    useEffect(() => { numberRef.current?.focus(); }, []);
    // Answers that arrive after the dialog closed are dropped.
    useEffect(() => () => { quoteSeq.current += 1; }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !creatingRef.current) onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    // A new order: start again from the reason.
    const begin = (lookup: ManualOrderLookup | null) => {
        setFound(lookup);
        setReasonCode(""); setPicked({}); setServiceId(null); setLabels(1); setDay(null); setNote(""); setAck(false);
        setReturnWarehouse(lookup?.order.return_to?.default_warehouse ?? null);
        setQuote(null); setQuoteError(null); setCreateError(null);
    };
    useEffect(() => { if (found) reasonRef.current?.focus(); }, [found]);

    const find = async (value: string) => {
        const q = value.trim();
        if (!q || finding) return;
        setNumber(q);
        setFinding(true);
        setFindError(null);
        try {
            const r = await findManualReturnOrder(q);
            if (r.ok && r.data) { begin(r.data); return; }
            begin(null);
            const refs: string[] = Array.isArray(r.body?.references) ? r.body.references.filter((x: unknown) => typeof x === "string") : [];
            if (r.notAvailable) setFindError({ message: t("rt_not_available"), references: [] });
            else if (r.code === "several_orders") setFindError({ message: r.message || t("rt_new_several"), references: refs });
            else setFindError({ message: r.message || t(r.status === 404 ? "rt_new_not_found" : "rt_new_find_failed"), references: [] });
        } finally {
            setFinding(false);
        }
    };
    const submitFind = (e: FormEvent) => { e.preventDefault(); void find(number); };

    const order: ManualOrder | null = found?.order ?? null;
    const items = useMemo(() => order?.items ?? [], [order]);
    const reasons = found?.reasons ?? [];
    const reason: ManualReason | null = reasons.find((r) => r.code === reasonCode) ?? null;

    const lines = useMemo(
        () => items.filter((it) => (picked[it.order_item_id] ?? 0) > 0).map((it) => ({ order_item_id: it.order_item_id, qty: picked[it.order_item_id] })),
        [items, picked],
    );
    const linesKey = JSON.stringify(lines);
    const orderRef = order?.reference ?? null;

    // Ask for the quote ~300 ms after the last change; an answer to an older question is ignored.
    useEffect(() => {
        const seq = ++quoteSeq.current;
        if (!orderRef || !reasonCode || lines.length === 0) {
            setQuote(null); setQuoteError(null); setQuoting(false);
            return undefined;
        }
        setQuoting(true);
        const timer = window.setTimeout(async () => {
            const r = await quoteManualReturn({
                order_reference: orderRef, reason: reasonCode, lines, service_id: serviceId, labels, collection_date: day,
                ...(returnWarehouse ? { return_warehouse: returnWarehouse } : {}),
            });
            if (seq !== quoteSeq.current) return;
            setQuoting(false);
            if (r.ok && r.data) { setQuote(r.data); setQuoteError(null); return; }
            setQuote(null);
            setQuoteError(r.notAvailable ? t("rt_not_available") : r.message || t("rt_new_quote_failed"));
        }, QUOTE_DELAY_MS);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderRef, reasonCode, linesKey, serviceId, labels, day, returnWarehouse, requote]);

    // Keep the choices in step with the latest quote: a service that is no longer eligible is
    // dropped (the only eligible one is chosen for you), labels stay within its maximum, and a
    // service with days always has one of its days.
    useEffect(() => {
        if (!quote) return;
        // The label by hand is always there to choose: kept whatever the quote offers.
        if (serviceId === BY_HAND_SERVICE) return;
        const q = quote.quote;
        const eligible = q.services.filter((s) => s.eligible);
        const current = eligible.find((s) => s.id === serviceId) ?? null;
        if (!current) {
            const only = eligible.length === 1 ? eligible[0] : null;
            if ((only?.id ?? null) !== serviceId) {
                setServiceId(only?.id ?? null);
                setDay(only?.dates?.[0] ?? null);
            }
            return;
        }
        const max = Math.max(1, q.max_labels);
        if (labels > max) setLabels(max);
        const dates = current.dates ?? [];
        if (dates.length > 0 && (!day || !dates.includes(day))) setDay(dates[0]);
        else if (dates.length === 0 && day) setDay(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quote]);

    // Every change by staff clears the last create error.
    const changed = () => setCreateError(null);
    const chooseReason = (code: string) => { changed(); setReasonCode(code); setAck(false); };
    const toggle = (it: ManualOrderItem, on: boolean) => {
        changed();
        setPicked((p) => {
            const next = { ...p };
            if (on && canPick(it)) next[it.order_item_id] = fullQty(it); else delete next[it.order_item_id];
            return next;
        });
    };
    const setQty = (it: ManualOrderItem, raw: string) => {
        changed();
        const n = Math.max(1, Math.min(it.available_qty, Math.floor(Number(raw) || 1)));
        setPicked((p) => ({ ...p, [it.order_item_id]: n }));
    };
    const pickable = items.filter(canPick);
    const allOn = pickable.length > 0 && pickable.every((it) => (picked[it.order_item_id] ?? 0) > 0);
    const someOn = pickable.some((it) => (picked[it.order_item_id] ?? 0) > 0);
    const toggleAll = () => {
        changed();
        if (allOn) { setPicked({}); return; }
        setPicked((p) => {
            const next = { ...p };
            for (const it of pickable) if (!next[it.order_item_id]) next[it.order_item_id] = fullQty(it);
            return next;
        });
    };
    useEffect(() => { if (allRef.current) allRef.current.indeterminate = someOn && !allOn; }, [someOn, allOn]);
    const chooseService = (s: ManualService) => { changed(); setServiceId(s.id); setDay(s.dates?.[0] ?? null); };
    const chooseByHand = () => { changed(); setServiceId(BY_HAND_SERVICE); setDay(null); setLabels(1); };
    // Another destination: its services differ (no Royal Mail away from the return warehouse, none abroad).
    const chooseDestination = (w: string) => { changed(); setReturnWarehouse(w); if (serviceId !== BY_HAND_SERVICE) { setServiceId(null); setDay(null); } };
    const destinations = order?.return_to?.options ?? [];
    const destination = destinations.find((d) => d.warehouse === returnWarehouse) ?? null;
    const returnHome = destinations.find((d) => d.return_warehouse) ?? null;

    // What the quote says now.
    const q = quote?.quote ?? null;
    const byHand = serviceId === BY_HAND_SERVICE;
    const service = q?.services.find((s) => s.id === serviceId) ?? null;
    const dates = service?.dates ?? [];
    const needsDay = dates.length > 0;
    const maxLabels = Math.max(1, q?.max_labels ?? 1);
    const email = quote ? quote.email : order?.customer.email ?? null;
    // The chosen reason's window (before the first quote, from the order's two windows).
    const win: ReturnWindow | null = quote?.window
        ?? (reason && order ? (reason.problem === "return" ? order.windows.mind : order.windows.problem) : null);
    const windowOpen = quote ? quote.window_ack_required : !!win?.open;
    const replaces = quote?.replaces ?? [];

    // The first thing still missing; null = ready to create.
    const blocker = (() => {
        if (!order) return t("rt_new_need_order");
        if (!reason) return t("rt_new_need_reason");
        if (lines.length === 0) return t("rt_new_need_items");
        if (quoting) return t("rt_new_quoting");
        if (!quote || quoteError) return t("rt_new_need_quote");
        if (!byHand && (!service || !service.eligible || q?.service_id !== service.id)) return t("rt_new_need_service");
        if (needsDay && !day) return t("rt_new_need_day");
        if (!email) return t("rt_new_no_email");
        if (windowOpen && !ack) return t("rt_new_need_ack");
        return null;
    })();

    const create = async () => {
        if (blocker || creating || !order || !quote) return;
        setCreating(true); creatingRef.current = true;
        setCreateError(null);
        try {
            const replace = replaces.map((x) => x.reference);
            const text = note.trim();
            const r = await createManualReturn({
                order_reference: order.reference, reason: reasonCode, lines, service_id: serviceId, labels,
                collection_date: needsDay ? day : null,
                ...(returnWarehouse ? { return_warehouse: returnWarehouse } : {}),
                ...(text ? { note: text } : {}),
                ...(windowOpen ? { acknowledge_window: true } : {}),
                ...(replace.length > 0 ? { replace } : {}),
            });
            if (r.ok && r.data) {
                const d = r.data;
                toast.success(d.label_by_hand
                    ? t("rt_new_done_by_hand", { reference: d.reference })
                    : d.auto_labels
                        ? t("rt_new_done", { reference: d.reference, email: d.email })
                        : t("rt_new_done_book", { reference: d.reference }));
                onCreated(d.reference);
                return;
            }
            setCreateError(r.notAvailable ? t("rt_not_available") : r.message || t("rt_new_create_failed"));
            if (r.code === "window_ack_required") setAck(false);
            if (r.code && REQUOTE_ON.has(r.code)) setRequote((n) => n + 1);
        } finally {
            setCreating(false); creatingRef.current = false;
        }
    };

    return (
        <div className="rt-modal-overlay">
            <div className="rt-modal rt-new" role="dialog" aria-modal="true" aria-labelledby={TITLE_ID}>
                <div className="rt-modal-head">
                    <h3 id={TITLE_ID}>{t("rt_new_title")}</h3>
                    <button type="button" className="rt-icon-btn" onClick={onClose} disabled={creating} aria-label={t("close")}><X size={16} /></button>
                </div>
                <div className="rt-modal-body">
                    <p className="rt-muted">{t("rt_new_intro")}</p>

                    {/* 1. The order */}
                    <form className="rt-new-find" onSubmit={submitFind} role="search">
                        <label className="rt-label rt-new-find-label" htmlFor="rt-new-number">{t("rt_new_order_number")}</label>
                        <input id="rt-new-number" ref={numberRef} className="rt-input rt-mono" value={number} autoComplete="off" spellCheck={false}
                            placeholder={t("rt_new_order_placeholder")} disabled={creating}
                            onChange={(e) => setNumber(e.target.value)} />
                        <button type="submit" className="btn btn-sm rt-btn btn-secondary" disabled={!number.trim() || finding || creating} aria-busy={finding}>
                            <Search size={14} /> {t("rt_new_find")}
                        </button>
                    </form>
                    {findError && (
                        <div className="rt-new-find-error" role="alert">
                            <p className="rt-error">{findError.message}</p>
                            {findError.references.length > 0 && (
                                <span className="rt-links">
                                    {findError.references.map((ref) => (
                                        <button key={ref} type="button" className="rt-chip rt-mono" onClick={() => void find(ref)}>{ref}</button>
                                    ))}
                                </span>
                            )}
                        </div>
                    )}

                    {order && (<>
                        <div className="rt-rts-meta rt-new-order" data-testid="rt-new-order">
                            <div>
                                <span className="rt-label">{t("rt_rts_order")}</span>
                                <strong className="rt-new-wrap">{order.order_numbers.map((n) => `#${n}`).join(", ") || order.reference}</strong>
                                {order.placed_on && <span className="rt-muted rt-small">{t("rt_new_placed", { date: fmtDate(order.placed_on) })}</span>}
                            </div>
                            <div>
                                <span className="rt-label">{t("rt_customer")}</span>
                                <span className="rt-new-wrap">{order.customer.name ?? "-"}</span>
                                <span className="rt-muted rt-small rt-new-wrap">{[order.customer.email, order.customer.postcode].filter(Boolean).join(" · ")}</span>
                            </div>
                            <div>
                                <span className="rt-label">{t("rt_new_delivery")}</span>
                                <span>{deliveryText(t, order, fmtDate)}</span>
                                <span className="rt-muted rt-small">
                                    {[order.carrier, order.parcels > 0 ? t("rt_new_parcels", { n: order.parcels }) : null].filter(Boolean).join(" · ")}
                                </span>
                            </div>
                        </div>

                        {/* 2. One reason for the whole return */}
                        <Step title={t("rt_new_reason")} htmlFor="rt-new-reason">
                            <div className="rt-new-reason">
                                <select id="rt-new-reason" ref={reasonRef} className="rt-input" value={reasonCode} disabled={creating}
                                    onChange={(e) => chooseReason(e.target.value)}>
                                    <option value="">{t("rt_new_reason_pick")}</option>
                                    {GROUPS.map((g) => {
                                        const rs = reasons.filter((r) => r.problem === g.problem);
                                        return rs.length > 0 ? (
                                            <optgroup key={g.problem} label={t(g.key)}>
                                                {rs.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                                            </optgroup>
                                        ) : null;
                                    })}
                                    {reasons.filter((r) => !GROUPS.some((g) => g.problem === r.problem)).map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                                </select>
                                {reason && (
                                    <span className={`rt-pill rt-pill--${reason.fault === "ours" ? "blue" : "grey"}`} data-testid="rt-new-fault">
                                        {t(reason.fault === "ours" ? "rt_fault_ours" : "rt_fault_customer")}
                                    </span>
                                )}
                            </div>
                            {windowOpen && win && (
                                <div className="rt-warn rt-new-window" role="note" data-testid="rt-new-window">
                                    <AlertTriangle size={14} aria-hidden="true" />
                                    <div>
                                        <strong className="rt-block">{t("rt_new_window_title")}</strong>
                                        <span className="rt-block">{windowText(t, win, fmtDate)}</span>
                                        <label className="rs-check rt-new-ack">
                                            <input type="checkbox" checked={ack} disabled={creating} onChange={(e) => { changed(); setAck(e.target.checked); }} />
                                            <span><strong>{t("rt_new_window_ack")}</strong></span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </Step>

                        {/* 3. The items */}
                        <Step title={t("rt_new_items")}>
                            <label className="rt-new-all">
                                <input ref={allRef} type="checkbox" checked={allOn} disabled={pickable.length === 0 || creating} onChange={toggleAll} />
                                <span>{t("rt_new_select_all")}</span>
                                <span className="rt-muted">({pickable.length})</span>
                            </label>
                            {pickable.length === 0 && <p className="rt-muted rt-small">{t("rt_new_nothing_to_return")}</p>}
                            <div className="rt-new-items">
                                {items.map((it) => (
                                    <ItemRow key={it.order_item_id} it={it} t={t} qty={picked[it.order_item_id] ?? 0} disabled={creating}
                                        onToggle={(on) => toggle(it, on)} onQty={(raw) => setQty(it, raw)} />
                                ))}
                            </div>
                        </Step>

                        {/* 4. Carrier, labels and the day */}
                        <Step title={t("rt_new_service")}>
                            {/* Where it goes back to: the warehouse that shipped it by default (Jack, 2026-11-04). */}
                            {destinations.length > 0 && (
                                <div className="rt-new-dest" data-testid="rt-new-dest">
                                    <label className="rt-new-field">
                                        <span className="rt-label">{t("rt_new_dest")}</span>
                                        <select className="rt-input" value={returnWarehouse ?? ""} disabled={creating}
                                            onChange={(e) => chooseDestination(e.target.value)}>
                                            {destinations.map((d) => (
                                                <option key={d.warehouse} value={d.warehouse}>
                                                    {d.warehouse === order?.return_to?.default_warehouse ? t("rt_new_dest_shipped", { warehouse: d.warehouse }) : d.warehouse}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    {destination && (
                                        <address className="rt-muted rt-small rt-new-dest-address">
                                            {[destination.address.company, destination.address.line_1, destination.address.line_2, destination.address.city, destination.address.postcode, destination.address.country]
                                                .filter(Boolean).join(", ")}
                                        </address>
                                    )}
                                    {destination && !destination.uk && <p className="rt-warn"><AlertTriangle size={14} /> {t("rt_new_dest_abroad")}</p>}
                                    {destination && destination.uk && !destination.return_warehouse && returnHome && (
                                        <p className="rt-muted rt-small">{t("rt_new_dest_no_rm", { warehouse: returnHome.warehouse })}</p>
                                    )}
                                </div>
                            )}
                            {!q && <p className="rt-muted rt-small">{t("rt_new_service_wait")}</p>}
                            {q && (
                                <fieldset className="rt-choice rt-new-services" aria-label={t("rt_new_service")} aria-busy={quoting}>
                                    {q.services.map((s) => (
                                        <label key={s.id} className={`${!s.eligible ? "rt-disabled" : ""}${s.id === serviceId ? " rt-new-service--on" : ""}`.trim() || undefined}
                                            data-testid="rt-new-service">
                                            <input type="radio" name="rt-new-service" value={s.id} checked={s.id === serviceId}
                                                disabled={!s.eligible || creating} onChange={() => chooseService(s)} />
                                            <span>
                                                <strong>{s.carrier} · {s.name}</strong>
                                                <small>
                                                    {t("rt_new_fee", { fee: gbp(s.fee) })} · {q.customer_fault
                                                        ? t("rt_new_deducted", { amount: gbp(s.charge) })
                                                        : t("rt_new_free_return")}
                                                </small>
                                                {s.blurb && <small>{s.blurb}</small>}
                                                {!s.eligible && s.why && <small className="rt-error">{s.why}</small>}
                                                {s.tags.length > 0 && (
                                                    <span className="rt-new-tags">{s.tags.map((tag) => <span key={tag} className="rt-pill rt-pill--plain rt-pill--sm">{tag}</span>)}</span>
                                                )}
                                            </span>
                                        </label>
                                    ))}
                                    {/* Always there: a destination no service books (abroad), or the customer's own way. */}
                                    <label className={byHand ? "rt-new-service--on" : undefined} data-testid="rt-new-service-by-hand">
                                        <input type="radio" name="rt-new-service" value={BY_HAND_SERVICE} checked={byHand} disabled={creating} onChange={chooseByHand} />
                                        <span>
                                            <strong>{t("rt_new_by_hand")}</strong>
                                            <small>{t("rt_new_by_hand_hint")}</small>
                                        </span>
                                    </label>
                                </fieldset>
                            )}
                            {q && service && (
                                <div className="rt-new-row">
                                    <label className="rt-new-field">
                                        <span className="rt-label">{t("rt_new_labels")}</span>
                                        <select className="rt-input" value={labels} disabled={creating}
                                            onChange={(e) => { changed(); setLabels(Number(e.target.value) || 1); }}>
                                            {Array.from({ length: maxLabels }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                    </label>
                                    {needsDay && (
                                        <label className="rt-new-field">
                                            <span className="rt-label">{t(service.kind === "collection" ? "rt_new_collection_day" : "rt_new_dropoff_day")}</span>
                                            <select className="rt-input" value={day ?? ""} disabled={creating}
                                                onChange={(e) => { changed(); setDay(e.target.value || null); }}>
                                                {dates.map((d) => <option key={d} value={d}>{fmtDay(d)}</option>)}
                                            </select>
                                        </label>
                                    )}
                                    {q.weight_kg > 0 && <span className="rt-muted rt-small rt-new-weight">{t("rt_new_weight", { kg: q.weight_kg.toFixed(1) })}</span>}
                                </div>
                            )}
                            {service?.depot && <p className="rt-muted rt-small rt-new-wrap">{t("rt_new_depot", { depot: depotText(service.depot) })}</p>}
                        </Step>

                        {/* 5. The money, who pays the postage, where the label goes */}
                        <Step title={t("rt_money")}>
                            {quoteError && <p className="rt-error" role="alert">{quoteError}</p>}
                            {!q && !quoteError && <p className="rt-muted rt-small">{quoting ? t("rt_new_quoting") : t("rt_new_service_wait")}</p>}
                            {q && (
                                <div className="rt-new-summary" data-testid="rt-new-summary" aria-busy={quoting}>
                                    <dl className="rt-kv rt-kv--money">
                                        <dt>{t("rt_items_value")}</dt><dd>{gbp(q.items_value)}</dd>
                                        <dt>{t("rt_vat_incl")}</dt><dd>{gbp(q.vat)}</dd>
                                        <dt>{t("rt_delivery_refund")}</dt><dd>{gbp(q.delivery_refund)}</dd>
                                        <dt>{t("rt_postage")}</dt><dd>{q.postage ? `-${gbp(q.postage)}` : gbp(0)}</dd>
                                        <dt className="rt-strong">{t("rt_new_refund_total")}</dt><dd className="rt-strong">{gbp(q.refund_total)}</dd>
                                    </dl>
                                    <p className="rt-small">{t(byHand ? "rt_new_pays_by_hand" : q.customer_fault ? "rt_new_pays_customer" : "rt_new_pays_us")}</p>
                                </div>
                            )}
                            {email
                                ? <p className="rt-small rt-new-email"><Mail size={14} aria-hidden="true" /> <span>{t("rt_new_email_to", { email })}</span></p>
                                : <p className="rt-error" role="alert">{t("rt_new_no_email")}</p>}
                            {replaces.length > 0 && (
                                <div className="rt-warn rt-new-replaces" data-testid="rt-new-replaces">
                                    <AlertTriangle size={14} aria-hidden="true" />
                                    <div>
                                        <strong className="rt-block">{t("rt_new_replaces_title")}</strong>
                                        <ul>
                                            {replaces.map((x) => (
                                                <li key={x.reference}>
                                                    {t("rt_new_replaces_line", { ref: x.reference, status: t(`rt_status_${x.status}`) })}
                                                    {x.live_carriers.length > 0 ? ` (${t("rt_new_replaces_live", { carriers: x.live_carriers.join(", ") })})` : ""}
                                                    {x.released.length > 0 && (
                                                        <ul>
                                                            {x.released.map((rel) => (
                                                                <li key={`${rel.order_reference}-${rel.order_item_id}`}>{t("rt_new_released", { title: rel.title, qty: rel.qty })}</li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                            <label className="rt-label" htmlFor="rt-new-note">{reason?.needs_detail ? t("rt_new_note_detail") : t("rt_new_note")}</label>
                            <textarea id="rt-new-note" className="rt-input" rows={2} maxLength={NOTE_MAX} value={note} disabled={creating}
                                onChange={(e) => { changed(); setNote(e.target.value); }} aria-describedby="rt-new-note-hint" />
                            <p id="rt-new-note-hint" className="rt-muted rt-small">{t("rt_new_note_hint")}</p>
                        </Step>
                    </>)}

                    {createError && <p className="rt-error" role="alert" data-testid="rt-new-create-error">{createError}</p>}
                    <div className="rt-modal-foot rt-new-foot">
                        {blocker && <span className="rt-new-blocker" data-testid="rt-new-blocker">{blocker}</span>}
                        <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={onClose} disabled={creating}>{t("cancel")}</button>
                        <button type="button" className="btn btn-sm rt-btn btn-primary" disabled={!!blocker || creating} aria-busy={creating}
                            title={blocker ?? undefined} onClick={() => void create()}>
                            <Plus size={14} /> {t("rt_new_create")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ---- pieces -----------------------------------------------------------------------------

const Step = ({ title, htmlFor, children }: { title: string; htmlFor?: string; children: ReactNode }) => (
    <section className="rt-new-step">
        {htmlFor
            ? <label className="rt-h3" htmlFor={htmlFor}>{title}</label>
            : <h4 className="rt-h3">{title}</h4>}
        {children}
    </section>
);

/** One order line: tick it, how many, and anything that stops it coming back. */
const ItemRow = ({ it, t, qty, disabled, onToggle, onQty }: {
    it: ManualOrderItem; t: T; qty: number; disabled: boolean; onToggle: (on: boolean) => void; onQty: (raw: string) => void;
}) => {
    const can = canPick(it);
    const on = qty > 0;
    const replace = it.requests.filter((r) => r.state === "replace");
    const blocked = it.requests.filter((r) => r.state !== "replace");
    return (
        <div className={`rt-new-item${can ? "" : " rt-new-item--off"}`} data-testid="rt-new-item">
            <input type="checkbox" checked={on} disabled={!can || disabled} aria-label={t("rt_new_pick_item", { item: it.title })}
                onChange={(e) => onToggle(e.target.checked)} />
            {it.image_url ? <img className="rt-line-img" src={it.image_url} alt="" /> : <span className="rt-line-img" />}
            <div className="rt-line-main">
                <strong>{it.title}</strong>
                <span className="rt-mono rt-muted">
                    {it.sku} · {t("rt_new_ordered", { n: it.ordered_qty })} · {t("rt_exc_each", { price: gbp(it.unit_price) })}
                </span>
                {it.refunded_qty > 0 && <span className="rt-small rt-muted">{t("rt_new_refunded", { n: it.refunded_qty })}</span>}
                {it.on_its_way && <span className="rt-new-note rt-new-note--warn">{t("rt_new_on_its_way")}</span>}
                {replace.map((r) => (
                    <span key={r.reference} className="rt-new-note rt-new-note--warn" data-testid="rt-new-replace-note">
                        {t("rt_new_req_replace", { n: r.qty, ref: r.reference, status: t(`rt_status_${r.status}`) })}
                        {r.carriers.length > 0 ? ` ${t("rt_new_req_live", { carriers: r.carriers.join(", ") })}` : ""}
                    </span>
                ))}
                {blocked.map((r) => (
                    <span key={r.reference} className="rt-new-note rt-new-note--off" data-testid="rt-new-blocked-note">
                        {it.ordered_qty > 1
                            ? t("rt_new_blocked_units", { n: r.qty, why: whyText(t, r) })
                            : t("rt_new_blocked", { why: whyText(t, r) })}
                    </span>
                ))}
                {!can && blocked.length === 0 && <span className="rt-new-note rt-new-note--off">{t("rt_new_nothing_left")}</span>}
            </div>
            <div className="rt-new-qty">
                {can && it.available_qty > 1 && (
                    <>
                        <input type="number" className="rt-input" min={1} max={it.available_qty} step={1} value={on ? qty : fullQty(it)}
                            disabled={!on || disabled} aria-label={t("rt_new_qty_label", { sku: it.sku })}
                            onChange={(e) => onQty(e.target.value)} />
                        <span className="rt-muted">{t("rt_rts_of", { n: it.available_qty })}</span>
                    </>
                )}
            </div>
        </div>
    );
};

/** Why units in another request can't be returned again ("already in BFR-…, on its way back to us"). */
const whyText = (t: T, r: ManualItemRequest): string => {
    const why = r.why ?? (r.state === "shipped" ? "on_its_way" : "");
    return WHY.has(why) ? t(`rt_new_why_${why}`, { ref: r.reference }) : t("rt_new_why_other", { ref: r.reference, status: t(`rt_status_${r.status}`) });
};

/** Where the order is: "Delivered 3 Nov 2026 (4 day(s) ago)", "On its way to the customer · sent 2 Nov 2026", … */
const deliveryText = (t: T, o: ManualOrder, fmtDate: (v: string | null) => string): string => {
    if (o.delivery_state === "delivered") {
        if (o.delivered_on && o.days_since_delivery !== null) return t("rt_new_delivered_ago", { date: fmtDate(o.delivered_on), n: o.days_since_delivery });
        if (o.delivered_on) return t("rt_delivered_on", { date: fmtDate(o.delivered_on) });
    }
    const state = t(`rt_new_state_${o.delivery_state}`);
    return o.sent_on && o.delivery_state !== "not_shipped" ? `${state} · ${t("rt_rts_shipped", { date: fmtDate(o.sent_on) })}` : state;
};

/** The window warning's body: how long since delivery (or the order), and until when the customer can do it themselves. */
const windowText = (t: T, w: ReturnWindow, fmtDate: (v: string | null) => string): string => {
    const params = { date: fmtDate(w.return_by), days: w.days };
    if (w.basis === "delivered" && w.days_since_delivery !== null) return t("rt_new_window_delivered", { ...params, n: w.days_since_delivery });
    if (w.days_since_order !== null) return t("rt_new_window_ordered", { ...params, n: w.days_since_order });
    return t("rt_new_window_plain", params);
};

const depotText = (d: NonNullable<ManualService["depot"]>): string => {
    const address = Array.isArray(d.address) ? d.address : d.address ? [d.address] : [];
    return [d.name, ...address, d.postcode].filter(Boolean).join(", ");
};

export default NewReturnDialog;
